/**
 * PairingQuiz Adult Renderer
 * Click-based left/right pairing with SVG line connections.
 * Adult-style clean UI: Tailwind semantic colors, no Lottie, no 3D shadows.
 * Uses the usePairingQuiz headless hook for all logic.
 */

import { cn } from '../../../lib/utils'
import { KatexText, QuizResultFeedback, SubmitButton, QuizSolveDescription, QuizMedia, QuizTypeBadge } from '../shared'
import type { QuizSolveResult } from '../../../types/legacyQuiz'
import type { PairingQuizData } from '../hooks'
import { usePairingQuiz } from '../hooks'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { X } from 'lucide-react'

const LINE_COLOR_DEFAULT = '#3b82f6' // blue-500

interface PairingQuizProps {
  quizId: string | number
  quiz: PairingQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
  onAnswerChange?: (answers: string[]) => void
  disableSound?: boolean
  difficultyLevel?: number | null
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
}

export function PairingAdult({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel,
  hideExplanation = false,
}: PairingQuizProps) {
  const { t } = useTranslation('quiz')

  const {
    displayPairings,
    selected,
    lines,
    previewLine,
    hoveredPos,
    setHoveredPos,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    pairs,
    containerRef,
    leftRefs,
    rightRefs,
    handleSelect,
    handleClearPairByLeft,
    handleClearPairByRight,
    handleSubmit,
    recalcLines,
    getLineColor,
  } = usePairingQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
  })

  return (
    <div className="space-y-6">
      {/* Quiz type badge */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-2"
      >
        <QuizTypeBadge
          quizId={quizId}
          typeLabel={t('types.pairing.label')}
          difficultyLevel={difficultyLevel}
        />
      </motion.div>

      {/* Title + media (no problem display) */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className={cn(
          'p-6 bg-linear-to-br to-gray-50 rounded-2xl from-slate-50',
          'border border-gray-100 shadow-2xs',
          'text-2xl font-medium leading-relaxed text-gray-900'
        )}
      >
        <KatexText text={quiz.title} />
        <QuizMedia image={quiz.image} imageMobile={quiz.imageMobile} desmos={quiz.desmos} vars={quiz.vars} />
      </motion.div>

      {/* Pairing area: left column + SVG lines + right column */}
      <div ref={containerRef} className="relative flex">
        {/* Left column */}
        <div className="flex flex-col shrink-0 z-10">
          {pairs.left.map((item, index) => {
            const pairing = displayPairings[index]
            const isPaired = pairing !== null
            const isCurrent = selected?.pos === 'left' && selected?.index === index
            const isPending = selected !== null && selected.pos !== 'left'
            const isHovered = hoveredPos?.pos === 'left' && hoveredPos?.index === index
            const pairCorrect = isSolved ? solveResult?.corrects?.[index] : undefined

            const isTrue = isSolved && (shouldShowCorrectAnswers || pairCorrect === true)
            const isFalse = isSolved && !shouldShowCorrectAnswers && pairCorrect === false && isPaired

            return (
              <div
                key={`left-${index}`}
                className={cn('relative cursor-pointer flex items-center mb-4 z-10', {
                  'pointer-events-none': isSolved || isViewSolved,
                })}
                onMouseEnter={() => setHoveredPos({ pos: 'left', index })}
                onMouseLeave={() => setHoveredPos(null)}
              >
                {/* Box */}
                <div
                  ref={el => { leftRefs.current[index] = el }}
                  className={cn(
                    'w-[180px] md:w-[240px] gap-2 min-h-[72px] border-2 border-gray-300 text-lg font-medium text-gray-800 rounded-xl p-3 flex justify-center items-center transition-all',
                    (isHovered || isPending) && !isCurrent && !isTrue && !isFalse &&
                      'border-blue-400 text-blue-600',
                    isCurrent && 'text-blue-700 bg-blue-50 border-blue-500',
                    isTrue && 'border-green-400 bg-green-50 text-green-700',
                    isFalse && 'border-red-400 bg-red-50 text-red-600',
                  )}
                  onClick={() => handleSelect('left', index)}
                >
                  {item.image?.signedUrl && (
                    <img
                      src={item.image.signedUrl}
                      alt="pairing"
                      className="max-h-16 w-auto object-contain"
                      onLoad={recalcLines}
                    />
                  )}
                  {item.answer && (
                    <div className="text-center">
                      <KatexText text={item.answer} />
                    </div>
                  )}
                </div>

                {/* Dot connector */}
                <div
                  className={cn(
                    'group w-7 h-7 absolute -right-3.5 top-1/2 -translate-y-1/2 overflow-hidden bg-gray-100 text-gray-500 text-xs font-semibold border-2 border-gray-300 rounded-full flex items-center justify-center z-20 cursor-pointer transition-all',
                    (isHovered || isPending) && !isCurrent && !(isCurrent || isPaired) && !isTrue && !isFalse &&
                      'bg-blue-50 border-blue-400 text-blue-500',
                    (isCurrent || isPaired) && !isTrue && !isFalse &&
                      'bg-blue-500 border-transparent text-white',
                    isTrue && 'border-transparent bg-green-500 text-white',
                    isFalse && 'border-transparent bg-red-500 text-white',
                  )}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (isPaired) {
                      handleClearPairByLeft(index)
                    } else {
                      handleSelect('left', index)
                    }
                  }}
                >
                  {index + 1}
                  {isPaired && !isSolved && (
                    <X
                      className="hidden group-hover:block absolute w-5 h-5 text-white bg-blue-500"
                      strokeWidth={3}
                    />
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* SVG Lines area */}
        <div className="relative grow w-full min-w-[40px]" />

        {/* SVG overlay for lines */}
        <svg
          className="absolute inset-0 pointer-events-none z-0"
          style={{ width: '100%', height: '100%', overflow: 'visible' }}
        >
          {lines.map((line, i) => (
            <line
              key={i}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              stroke={getLineColor(line.leftIdx)}
              strokeWidth={2.5}
              strokeLinecap="round"
            />
          ))}
          {/* Dashed preview line */}
          {previewLine && (
            <line
              x1={previewLine.x1}
              y1={previewLine.y1}
              x2={previewLine.x2}
              y2={previewLine.y2}
              stroke={LINE_COLOR_DEFAULT}
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeDasharray="6 6"
            />
          )}
        </svg>

        {/* Right column */}
        <div className="flex flex-col shrink-0 z-10">
          {pairs.right.map((item, index) => {
            const isCurrent = selected?.pos === 'right' && selected?.index === index
            const isPending = selected !== null && selected.pos !== 'right'
            const isHovered = hoveredPos?.pos === 'right' && hoveredPos?.index === index

            const pairedLeftIdx = displayPairings.findIndex(p => p?.rightIndex === index)
            const isPairedByAnyLeft = pairedLeftIdx !== -1

            let isTrue = false
            let isFalse = false
            let dotNum: string | number = ''

            if (shouldShowCorrectAnswers) {
              const correctIdx = solveResult?.correctAnswers?.findIndex(key => key === item.key) ?? -1
              if (correctIdx !== -1) {
                isTrue = true
                dotNum = correctIdx + 1
              }
            } else if (isSolved && isPairedByAnyLeft) {
              const pairCorrect = solveResult?.corrects?.[pairedLeftIdx]
              isTrue = pairCorrect === true
              isFalse = pairCorrect === false
              dotNum = pairedLeftIdx + 1
            } else if (!isSolved && isPairedByAnyLeft) {
              dotNum = pairedLeftIdx + 1
            }

            return (
              <div
                key={`right-${index}`}
                className={cn('relative cursor-pointer flex items-center flex-row-reverse mb-4 z-10', {
                  'pointer-events-none': isSolved || isViewSolved,
                })}
                onMouseEnter={() => setHoveredPos({ pos: 'right', index })}
                onMouseLeave={() => setHoveredPos(null)}
              >
                {/* Box */}
                <div
                  ref={el => { rightRefs.current[index] = el }}
                  className={cn(
                    'w-[180px] md:w-[240px] gap-2 min-h-[72px] border-2 border-gray-300 text-lg font-medium text-gray-800 rounded-xl p-3 flex justify-center items-center transition-all',
                    (isHovered || isPending) && !isCurrent && !isTrue && !isFalse &&
                      'border-blue-400 text-blue-600',
                    isCurrent && 'text-blue-700 bg-blue-50 border-blue-500',
                    isTrue && 'border-green-400 bg-green-50 text-green-700',
                    isFalse && 'border-red-400 bg-red-50 text-red-600',
                  )}
                  onClick={() => handleSelect('right', index)}
                >
                  {item.image?.signedUrl && (
                    <img
                      src={item.image.signedUrl}
                      alt="pairing"
                      className="max-h-16 w-auto object-contain"
                      onLoad={recalcLines}
                    />
                  )}
                  {item.answer && (
                    <div className="text-center">
                      <KatexText text={item.answer} />
                    </div>
                  )}
                </div>

                {/* Dot connector */}
                <div
                  className={cn(
                    'group w-7 h-7 absolute -left-3.5 top-1/2 -translate-y-1/2 overflow-hidden bg-white text-gray-500 text-xs font-semibold border-2 border-gray-300 rounded-full flex items-center justify-center z-20 cursor-pointer transition-all',
                    isPending && !isCurrent && !(isCurrent || isPairedByAnyLeft) && !isTrue && !isFalse &&
                      'bg-blue-50 border-blue-400 text-blue-500',
                    isHovered && !isCurrent && !(isCurrent || isPairedByAnyLeft) && !isTrue && !isFalse &&
                      'bg-white border-blue-400 text-blue-500',
                    (isCurrent || isPairedByAnyLeft) && !isTrue && !isFalse &&
                      'bg-blue-500 border-transparent text-white',
                    isTrue && 'border-transparent bg-green-500 text-white',
                    isFalse && 'border-transparent bg-red-500 text-white',
                  )}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (isPairedByAnyLeft) {
                      handleClearPairByRight(index)
                    } else {
                      handleSelect('right', index)
                    }
                  }}
                >
                  {dotNum}
                  {isPairedByAnyLeft && !isSolved && (
                    <X
                      className="hidden group-hover:block absolute w-5 h-5 text-white bg-blue-500"
                      strokeWidth={3}
                    />
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Submit button */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel}
        />
      )}

      {/* Result feedback */}
      {isSolved && (
        <QuizResultFeedback
          isCorrect={solveResult?.correct ?? false}
          show={isSolved}
          disableSound={disableSound}
          isViewSolved={isViewSolved}
        />
      )}

      {/* Solve description */}
      {isSolved && !hideExplanation && (
        <QuizSolveDescription
          description={solveResult?.description ?? undefined}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={!solveResult?.correct}
          actionLabel={isShowingCorrectAnswers ? t('common.viewMyAnswer') : t('common.viewCorrectAnswer')}
          onActionClick={() => setIsShowingCorrectAnswers(v => !v)}
        />
      )}
    </div>
  )
}
